import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {take} from 'rxjs/operators';
import {CommandService} from '@core/services/command.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {DialogModel} from '@shared/models/dialog.model';
import {SnackBarService} from '@core/services/snack-bar.service';
import {
  AddUserToSubAccountCommand,
  EditSubAccountUserPackagesCommand,
  EnableSubAccountUserCommand,
  SendConfirmationEmailCommand,
  UpdateSubAccountUserStatusCommand
} from '@features/firm/commands/firm.commands';
import {FirmApiService} from '@features/firm/services/firm-service';

@Injectable({
  providedIn: 'root',
})
export class SubAccountUsersHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  magicLinkModelValueChanged = new EventEmitter<any>();

  constructor(private commandService: CommandService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private router: Router,
              private dialogService: DialogService,
              private snackBarService: SnackBarService,
              private firmApiService: FirmApiService) {
  }

  destroy(): void {
  }

  onBeforeLoad(): void {
    this.normalizeGridTabData();
  }

  /** Ensures tabData keys match what firm grid host and commands expect (handles JSON camelCase). */
  private normalizeGridTabData(): void {
    const tab = this.gridTab();
    const td = tab?.tabData as Record<string, unknown> | undefined;
    if (!td) {
      return;
    }
    const accountId = td['ACCOUNT_ID'] ?? td['accountId'] ?? td['AccountId'];
    if (accountId != null && accountId !== '') {
      td['ACCOUNT_ID'] = String(accountId);
    }
    const st = td['SourceType'] ?? td['sourceType'];
    if (st != null && st !== '') {
      td['SourceType'] = String(st);
    }
    const api = td['Api'] ?? td['api'];
    if (api != null && api !== '') {
      td['Api'] = String(api);
    }
  }

  private gridTab(): TabModel | undefined {
    const tabs = this.magicLinkModel?.tabBar?.tabs;
    return tabs?.find(t => t.editor === 'FirmSubAccountUsersGridHostComponent') ?? tabs?.[0];
  }

  executeCommand(command: PlatformCommand, callBack?: any) {
    if (command.name === 'AddUserToSubAccountDialogCommand') {
      this.openAddUserDialog();
      return;
    }
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(() => {
          this.invalidateSubject?.invalidate();
          this.fuseSplashScreenService.hide();
        },
        error => {
          console.log(error);
          this.fuseSplashScreenService.hide();
        });
    }
  }

  valueChanged($event: any) {
    if ($event.fieldDefinition && $event.fieldDefinition.key !== 'Actions') {
      this.dialogService.showCellEditorDialog($event.item, $event.fieldDefinition).pipe(take(1)).subscribe(result => {
        if (result.action === 'OK') {
          console.log(result);
        }
      });
    }

    switch ($event.action && $event.action.id) {
      case 'Delete':
        this.deleteSubAccountUser($event);
        break;
      case 'Disable':
        this.disableSubAccountUser($event);
        break;
      case 'Enable':
        this.enableSubAccountUser($event);
        break;
      case 'SendConfirmationEmail':
        this.sendConfirmationEmailToSubAccountUser($event);
        break;
      case 'EditPackages':
        this.editSubAccountUserPackages($event);
        break;
    }
  }

  setSelectedTab(selectedTab: TabModel): void {
  }

  private accountId(): string {
    const gridTab = this.gridTab();
    const fromTab = gridTab?.tabData?.['ACCOUNT_ID'] as string;
    if (fromTab) {
      return fromTab;
    }
    const m = this.router.url.match(/\/firm\/sub-accounts\/([^/?]+)/);
    return m ? m[1] : '';
  }

  private openAddUserDialog() {
    const dialogModel = DialogModel.create('Add User', 'AddUserDialogComponent', {}, '600px', '800px');
    this.dialogService.showOkCancelDialog(dialogModel).pipe(take(1)).subscribe(state => {
      if (state.action === 'OK') {
        const input = state.data.input;
        const id = this.accountId();
        const command = new AddUserToSubAccountCommand(id, input.firstName, input.lastName, input.email, input.phone, input.title, input.message, input.packages);
        this.commandService.executeCommand(command).pipe(take(1)).subscribe({
          next: () => {
            this.snackBarService.showInformation('User added successfully.');
            this.invalidateSubject?.invalidate();
          },
          error: () => {
            this.snackBarService.showError('Failed to add user.');
          }
        });
      }
    });
  }

  private editSubAccountUserPackages(event: any) {
    const userId = event.item.id;
    const rawPackages = event.item?.cells?.['Packages']?.value;
    const currentPackages: string[] = Array.isArray(rawPackages)
      ? rawPackages
      : (typeof rawPackages === 'string' && rawPackages.trim()
        ? rawPackages.split(',').map(p => p.trim()).filter(Boolean)
        : []);
    const dialogModel = DialogModel.create('Edit Packages', 'EditPackagesDialogComponent', {currentPackages}, '400px', '350px');
    this.dialogService.showOkCancelDialog(dialogModel).pipe(take(1)).subscribe(state => {
      if (state.action === 'OK') {
        const packages: string[] = state.data.input.packages ?? [];
        const cmd = new EditSubAccountUserPackagesCommand(userId, packages);
        this.commandService.executeCommand(cmd).pipe(take(1)).subscribe({
          next: () => {
            this.snackBarService.showInformation('Packages updated successfully.');
            this.invalidateSubject?.invalidate();
          },
          error: () => {
            this.snackBarService.showError('Failed to update packages.');
          }
        });
      }
    });
  }

  sendConfirmationEmailToSubAccountUser(event: any) {
    const title = 'Send Confirmation Email';
    const message = 'Are you sure you want to send confirmation email?';
    this.dialogService.showYesNoDialog(title, message).pipe(take(1)).subscribe(result => {
      if (result.action === 'OK') {
        const userId = event.item.id;
        const cmd = new SendConfirmationEmailCommand(userId);
        this.commandService.executeCommand(cmd).pipe(take(1)).subscribe(() => {
          this.invalidateSubject?.invalidate();
        });
      }
    });
  }

  deleteSubAccountUser(event: any) {
    const userId = event.item.id;
    const userStatus = 'Deleted';
    const title = 'Delete Sub Account User';
    const message = 'Are you sure you want to delete this user?';
    this.updateUserStatus(userId, userStatus, title, message);
  }

  disableSubAccountUser(event: any) {
    const userId = event.item.id;
    const userStatus = 'Disabled';
    const title = 'Disable Sub Account User';
    const message = 'Are you sure you want to disable this user?';
    this.updateUserStatus(userId, userStatus, title, message);
  }

  enableSubAccountUser(event: any) {
    const title = 'Enable Sub Account User';
    const message = 'Are you sure you want to enable this user?';
    this.dialogService.showYesNoDialog(title, message).pipe(take(1)).subscribe(result => {
      if (result.action === 'OK') {
        const userId = event.item.id;
        const cmd = new EnableSubAccountUserCommand(userId);
        this.commandService.executeCommand(cmd).pipe(take(1)).subscribe(() => {
          this.invalidateSubject?.invalidate();
        });
      }
    });
  }

  updateUserStatus(userId: any, userStatus: string, title: string, message: string) {
    this.dialogService.showYesNoDialog(title, message).pipe(take(1)).subscribe(result => {
      if (result.action === 'OK') {
        const cmd = new UpdateSubAccountUserStatusCommand(userId, userStatus);
        this.commandService.executeCommand(cmd).pipe(take(1)).subscribe(() => {
          this.invalidateSubject?.invalidate();
        });
      }
    });
  }
}
